import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import ModalWindow from '../../../ModalWindow';
import AddNewDocument from '../../../PopupForms/AddNewDocument';
import { ProfileService } from '../../../../service/ProfileService';
import DeleteModal from '../../../PopupForms/DeleteDialog/DeleteModal';
import Pagination from 'components/common/Layout/Pagination';
import { EMPLOYEE_ROLES } from 'constants/user';
import { ITEMS_PER_PAGE } from 'constants/settings';
import { showErrorMessage } from 'helpers/notifications';

import styles from './styles.module.scss';
import DownloadDocumentButton from 'components/common/DownloadDocumentButton';
import Loading from 'components/Loading';
import { getOffsetAfterDeletion } from 'utils/utils';
import {
  groupDocuments,
  getDocumentStatus,
  formatDateValue,
  isUploadOnlyDocument,
  getDocumentProfile,
  DOCUMENT_CATEGORY_KEYS,
} from 'utils/documentGroups';
import {
  resolveVerificationState,
  canRequestVerification,
} from 'utils/documentVerification';
import { getVerificationStatus, requestVerification } from 'store/verification';
import VerificationBanner from './VerificationBanner';

const shieldIcon = new URL('../../../../img/shield-outline.svg', import.meta.url).href;
const licenseIcon = new URL('../../../../img/license-card.svg', import.meta.url).href;
const fileIcon = new URL('../../../../img/file-outline.svg', import.meta.url).href;
const eyeIcon = new URL('../../../../img/eye.svg', import.meta.url).href;
const downloadIcon = new URL('../../../../img/download-white.svg', import.meta.url).href;

const UploadButton = ({ onClick }: { onClick?: () => void }) => {
  const { t } = useTranslation();
  return (
    <button className={styles.uploadButton} type="button" onClick={onClick}>
      {t('documents.upload_button')}
    </button>
  );
};

const SECTION_ICONS: Record<string, string> = {
  [DOCUMENT_CATEGORY_KEYS.REGISTRATION]: fileIcon,
  [DOCUMENT_CATEGORY_KEYS.INSURANCE]: shieldIcon,
  [DOCUMENT_CATEGORY_KEYS.LICENSES]: licenseIcon,
  [DOCUMENT_CATEGORY_KEYS.OTHER]: fileIcon,
};

const ProfileDocuments = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const user = useSelector((state: any) => state.profile.profileData);
  const company = useSelector((state: any) => state.company.companyData);
  const lang = useSelector((state: any) => state.settings.lang);
  /** Статус проверки с бэкенда: только он знает про заявку и причину отказа */
  const verificationData = useSelector((state: any) => state.verification.data);

  const canEditDocuments = user.employee_company_roles === EMPLOYEE_ROLES.OWNER;
  /** Роль компании решает, какие документы от неё требуются */
  const companyRole = company.company_roles || user.company_roles;
  const documentProfile = getDocumentProfile(companyRole);

  const [isLoading, setIsLoading] = useState(true);
  const [expandedDocId, setExpandedDocId] = useState<string | number | null>(null);
  const [isSubmittingVerification, setIsSubmittingVerification] = useState(false);
  const [documents, setDocuments] = useState<{ items: any[]; count: number }>({
    items: [],
    count: 0,
  });

  const loadData = useCallback(
    (offset = 0) => {
      const params = new URLSearchParams(location.search);
      const urlOffset = offset || parseInt(params.get('offset') || '0') || 0;

      setIsLoading(true);
      ProfileService.getDocuments({
        limit: ITEMS_PER_PAGE,
        offset: urlOffset,
        lang,
      })
        .then((res: any) => {
          setDocuments(res.data);
        })
        .catch((e: any) => {
          console.log(e.response);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [location.search, lang],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    dispatch(getVerificationStatus() as any);
  }, [dispatch]);

  const handleAddDocument = (data: FormData, closeCallback: () => void) => {
    ProfileService.addDocument(data)
      .then(() => {
        closeCallback();
        loadData();
      })
      .catch((e: any) => {
        const errorData = e.response?.data;
        const detail = errorData?.detail || errorData?.title || e.message || t('errors.unknown');
        const errors = errorData?.errors?.map((err: any) => err.message).join('; ');
        showErrorMessage(errors || detail);
        console.error('Document upload error:', errorData || e);
      });
  };

  const handleDeleteDocument = (id: number, closeCallback: () => void) => {
    ProfileService.deleteDocument(id)
      .then(() => {
        closeCallback();
        const params = new URLSearchParams(location.search);
        const urlOffset = parseInt(params.get('offset') || '0') || 0;
        const newOffset = getOffsetAfterDeletion(
          documents.count,
          urlOffset,
          ITEMS_PER_PAGE,
          documents.items.length,
        );
        if (newOffset !== null) {
          const searchParams = new URLSearchParams(location.search);
          searchParams.set('offset', newOffset.toString());
          navigate({
            ...location,
            search: newOffset as any,
          });
        }
        loadData(newOffset as number);
      })
      .catch((e: any) => {
        console.log(e.response);
      });
  };

  const handleRequestVerification = () => {
    setIsSubmittingVerification(true);
    (dispatch(requestVerification() as any) as any)
      .unwrap()
      .catch((errorData: any) => {
        const errors = errorData?.errors?.map((err: any) => err.message).join('; ');
        showErrorMessage(errors || errorData?.detail || errorData?.title || t('errors.unknown'));
      })
      .finally(() => {
        setIsSubmittingVerification(false);
      });
  };

  const hasPagination = documents.count / ITEMS_PER_PAGE > 1;
  const groupedDocuments = groupDocuments(documents.items, { includeEmptyCategories: true, companyRole });
  const verification = resolveVerificationState({
    documents: documents.items,
    apiStatus: verificationData?.status || company.verification_status,
    companyRole,
  });
  const verificationComment = verificationData
    ? verificationData.comment
    : company.verification_comment;
  const showVerificationSubmit =
    canEditDocuments && canRequestVerification(verification.state, verification.missing.length);

  // Build a map of uploaded documents by displayLabel for quick lookup
  const uploadedDocsByLabel: Record<string, any> = {};
  documents.items.forEach((doc: any) => {
    const label = doc.displayLabel || doc.status || doc.file_name || '';
    uploadedDocsByLabel[label] = doc;
  });

  const renderDocumentCard = (docLabel: string, uploadedDoc: any, sectionKey: string, index: number) => {
    const isUploaded = !!uploadedDoc;

    if (isUploaded) {
      const status = getDocumentStatus(uploadedDoc, t);
      const isUploadOnly = isUploadOnlyDocument(uploadedDoc);
      const validFrom = isUploadOnly ? null : formatDateValue(uploadedDoc.valid_from);
      const validTo = isUploadOnly ? null : formatDateValue(uploadedDoc.valid_to);
      const coverageAmount = uploadedDoc.coverage_amount || null;
      const policyNumber = uploadedDoc.policy_number || null;
      const uploadedAt = formatDateValue(uploadedDoc.created_at || uploadedDoc.updated_at);
      const documentName = uploadedDoc.displayLabel || docLabel;
      const hasDetails = Boolean(validFrom || validTo || coverageAmount || policyNumber);
      const isExpanded = expandedDocId === uploadedDoc.id;

      return (
        <div className={styles.docCardUploaded} key={`${sectionKey}-${index}`}>
          <div className={styles.docInfo}>
            <span className={styles.docNameStrong}>{documentName}</span>
            <div className={styles.metaRow}>
              {status && (
                <span className={cn(styles.statusBadge, styles[`statusBadge--${status.tone}`])}>
                  {status.label}
                </span>
              )}
              {uploadedAt && (
                <span className={styles.metaMuted}>
                  {t('documents.uploaded_on', { date: uploadedAt })}
                </span>
              )}
            </div>
            {isExpanded && hasDetails && (
              <div className={styles.detailsBlock}>
                {(validFrom || validTo) && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>{t('documents.validity_period')}:</span>
                    {' '}{[validFrom, validTo].filter(Boolean).join(' – ')}
                  </span>
                )}
                {coverageAmount && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>{t('documents.modal.coverage_amount')}:</span>
                    {' '}{coverageAmount}
                  </span>
                )}
                {policyNumber && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>{t('documents.modal.policy_number')}:</span>
                    {' '}{policyNumber}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className={styles.docActions}>
            {hasDetails && (
              <button
                type="button"
                className={styles.detailsToggle}
                onClick={() => setExpandedDocId(isExpanded ? null : uploadedDoc.id)}
              >
                {t(isExpanded ? 'documents.hide_details' : 'documents.show_details')}
              </button>
            )}
            {uploadedDoc.document_url && (
              <a
                className={styles.iconButton}
                href={uploadedDoc.document_url}
                target="_blank"
                rel="noreferrer"
                title={t('documents.open_document')}
              >
                <img src={eyeIcon} alt="" />
              </a>
            )}
            <DownloadDocumentButton
              url={uploadedDoc.document_url}
              text={<img src={downloadIcon} alt="" />}
              fileName={uploadedDoc.document_url?.split('/').pop() || uploadedDoc.status}
              className={cn(styles.iconButton, styles['iconButton--download'])}
            />
            {canEditDocuments && (
              <span className={styles.deleteSlot}>
                <DeleteModal
                  onDeleteClick={handleDeleteDocument}
                  data={{
                    title: t('documents.deleteDocument'),
                    id: uploadedDoc.id,
                    type: t('documents.document'),
                    name: documentName,
                  }}
                />
              </span>
            )}
          </div>
        </div>
      );
    }

    // Empty document card (not uploaded)
    return (
      <div className={styles.docCard} key={`${sectionKey}-${index}`}>
        <div className={styles.docInfo}>
          <span className={styles.docName}>{docLabel}</span>
          <span className={styles.docStatus}>{t('documents.not_provided')}</span>
        </div>
        {canEditDocuments && (
          <div className={styles.docActions}>
            <ModalWindow
              WindowComponent={AddNewDocument}
              ButtonComponent={UploadButton}
              addClick={handleAddDocument}
              documents={documents.items}
              mobileSheet
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      {!isLoading && (
        <VerificationBanner
          state={verification.state}
          missing={verification.missing}
          comment={verificationComment}
          onSubmit={showVerificationSubmit ? handleRequestVerification : undefined}
          isSubmitting={isSubmittingVerification}
        />
      )}

      {isLoading ? (
        <div className={styles.loadingWrapper}>
          <Loading />
        </div>
      ) : (
        groupedDocuments.map((section) => {
          const requiredTypes = documentProfile.requiredTypes[section.categoryKey] || [];
          const sectionIcon = SECTION_ICONS[section.categoryKey];

          // Merge required types with uploaded docs
          const uploadedByLabel: Record<string, any> = {};
          section.items.forEach((item: any) => {
            uploadedByLabel[item.displayLabel] = item;
          });

          // Build card list: required types first, then any extra uploaded docs
          const requiredLabels = new Set(requiredTypes);
          const cards = requiredTypes.map((label: string) => ({
            label,
            uploaded: uploadedByLabel[label] || null,
          }));

          // Add any uploaded docs that don't match required types
          section.items.forEach((item: any) => {
            if (!requiredLabels.has(item.displayLabel)) {
              cards.push({ label: item.displayLabel, uploaded: item });
            }
          });

          return (
            <div className={styles.section} key={section.categoryKey}>
              <div className={styles.sectionHeader}>
                {sectionIcon && (
                  <img className={styles.sectionIcon} src={sectionIcon} alt="" />
                )}
                <span className={styles.sectionTitle}>{t(section.titleKey)}</span>
              </div>
              {cards.map((card, index) =>
                renderDocumentCard(card.label, card.uploaded, section.categoryKey, index),
              )}
            </div>
          );
        })
      )}

      {hasPagination && (
        <Pagination itemsOnPage={ITEMS_PER_PAGE} count={documents.count} url="/profile/documents" />
      )}
    </div>
  );
};

export default ProfileDocuments;
